import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import {
    Box,
    createStyles, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, Icon,
    IconButton, Input, InputAdornment, InputLabel, makeStyles, MenuItem, Radio, RadioGroup, Select, TextField, Theme, Typography
} from '@material-ui/core';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { InfluencerContext } from '../NewsContext';

import { ClassificationInfluencerModel } from '../../../Models/ClassificationInfluencerModel';
import { HomeContext } from '../../Home/HomeContext';
import { NewsLoadingContext } from '../NewsLoadingContext';
import { InfluencerModel } from '../../../Models/InfluencerModel';
import { InfluencersResponse } from '../../../Models/InfluencerResponseModel';
import InfluencerService from '../../../Services/InfluencerService';

const useStyles = makeStyles((theme: Theme) => createStyles({
    itens: {
        width: '100%',
        color: 'primary',
    },
    itemData: {
        width: '100%',
        color: 'primary',
        paddingBottom: theme.spacing(1.5),

    },
    linkColor: {
        color: '#2C64D0',
        textOverflow: 'ellipsis',
        '&:hover': {
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },
    editText: {
        textOverflow: 'ellipsis',
    },
    yellowBackground: {
        backgroundColor: theme.palette.warning.light
    },
    outsideGrid: {
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    loading: {
        minHeight: '20vh'
    },
    linksPaperError: {
        padding: theme.spacing(3),
        border: `1px solid ${theme.palette.error.main}`,
    },
    errorText: {
        paddingLeft: theme.spacing(2)
    }
}));

export default function NewsData() {
    const classes = useStyles();

    const newsContext = React.useContext(InfluencerContext);
    const newsLoadingContext = React.useContext(NewsLoadingContext);
    const homeContext = React.useContext(HomeContext);

    const [loading, setLoading] = React.useState<boolean>(false);

    const [nomeDisabled, setNomeDisabled] = React.useState(true);
    const [nomeFocused, setNomeFocused] = React.useState(false);

    const [urlPerfilDisabled, setUrlPerfilDisabled] = React.useState(true);
    const [urlPerfilFocused, setUrlPerfilFocused] = React.useState(false);

    const [observacaoStatusDisabled, setObservacaoStatusDisabled] = React.useState(true);
    const [observacaoStatusFocused, setObservacaoStatusFocused] = React.useState(false);

    const [objetivoUsoDisabled, setObjetivoUsoDisabled] = React.useState(true);
    const [objetivoUsoFocused, setObjetivoUsoFocused] = React.useState(false);


    const [campanhaDisabled, setCampanhaDisabled] = React.useState(true);
    const [campanhaFocused, setCampanhaFocused] = React.useState(false);

    const [pagoOrgStatusDisabled, setPagoOrgStatusDisabled] = React.useState(true);
    const [pagoOrgStatusFocused, setPagoOrgStatusFocused] = React.useState(false);

    const [tipoPerfilStatusDisabled, setTipoPerfilStatusDisabled] = React.useState(true);
    const [tipoPerfilStatusFocused, setTipoPerfilStatusFocused] = React.useState(false);

    const [nomeNotDefinedError, setNomeNotDefinedError] = React.useState(false);
    const [urlNotDefinedError, setUrlNotDefinedError] = React.useState(false);

    const [filters, setFilters] = React.useState<InfluencersResponse>();

    React.useEffect(() => {
        setLoading(true);
        InfluencerService.getInfluencerFiltersClassification(newsContext.news.id)
            .then((res: AxiosResponse) => {
                setFilters(res.data);

                setLoading(false);
            })
            .catch((error: AxiosError) => { setLoading(false); homeContext.showError('', error); });

    }, []);

    const handleChange = (prop: keyof ClassificationInfluencerModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
        newsContext.setNews({ ...newsContext.news, [prop]: event.target.value });
    };

    const handleChangeSelect = (prop: keyof ClassificationInfluencerModel) =>
        (event: React.ChangeEvent<{ value: unknown }>) => {
            newsContext.setNews({ ...newsContext.news, [prop]: event.target.value as string });
        };


    const getDate = () => {
        return (
            <Grid item xs={6}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <DatePicker
                        id="news-date"
                        variant="inline"
                        value={newsContext.news.dataAnalise}
                        inputVariant="outlined"
                        label="Data de upload"
                        minDate={new Date('0000-00-00')}
                        onChange={(date) =>
                            date !== null ? '' : ''
                        }
                        InputProps={{
                            readOnly: true,
                        }}
                        format="dd/MM/yyyy"
                        className={classes.itemData}
                    />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    const getNomeSolicitante = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="nomeSolicitante"
                        value={newsContext.news.id_solicitante}
                        onChange={() => { return; }}
                        label="Nome Solicitante"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };

    const getCampanha = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="campanha"
                        value={newsContext.news.campanha ?? ''}
                        onChange={handleChange('campanha')}
                        label="Campanha"
                        variant="outlined"
                        disabled={campanhaDisabled}
                        className={classes.itens}
                        onBlur={() => { setCampanhaDisabled(true); setCampanhaFocused(false); }}
                        inputRef={input => campanhaFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (campanhaDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setCampanhaDisabled(!campanhaDisabled); setCampanhaFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                    />
                </Grid>
            </>
        );
    };

    const getTamanho = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="tamanho"
                        value={newsContext.news.tamanho}
                        onChange={() => { return; }}
                        label="Tamanho"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };

    const getNomeFiguraPublica = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="nomeFiguraPublica"
                        value={newsContext.news.nome}
                        onChange={handleChange('nome')}
                        label="Nome da figura pública"
                        variant="outlined"
                        disabled={nomeDisabled}
                        className={classes.itens}
                        onBlur={() => { setNomeDisabled(true); setNomeFocused(false); }}
                        inputRef={input => nomeFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (nomeDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setNomeDisabled(!nomeDisabled); setNomeFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                        error={nomeNotDefinedError && !newsContext.news.nome}
                        helperText={nomeNotDefinedError && !newsContext.news.nome ? 'Campo obrigatório' : ''}
                    />
                </Grid>
            </>
        );
    };

    const ObjetivoUso = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="objetivoUso"
                        value={newsContext.news.objetivoUso ?? ''}
                        onChange={handleChange('objetivoUso')}
                        label="Objetivo de uso"
                        variant="outlined"
                        disabled={objetivoUsoDisabled}
                        className={classes.itens}
                        onBlur={() => { setObjetivoUsoDisabled(true); setObjetivoUsoFocused(false); }}
                        inputRef={input => objetivoUsoFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (objetivoUsoDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setObjetivoUsoDisabled(!objetivoUsoDisabled); setObjetivoUsoFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                    />
                </Grid>
            </>
        );
    };

    const getUsuario = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="usuario"
                        value={newsContext.news.usuario}
                        onChange={() => { return; }}
                        label="Usuario"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };


    const getRtb = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="news-rtb"
                        value={newsContext.news.rtb}
                        onChange={() => { return; }}
                        label="RTB"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };


    const getPreAssessment = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="news-pre-avaliacao"
                        value={newsContext.news.preavaliacao}
                        onChange={() => { return; }}
                        label="PreAvaliação"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };

    const getUrlPerfil = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="URL da figura pública"
                        value={newsContext.news.url_perfil}
                        onChange={handleChange('url_perfil')}
                        label="URL da figura pública"
                        variant="outlined"
                        disabled={urlPerfilDisabled}
                        className={classes.itens}
                        onBlur={() => { setUrlPerfilDisabled(true); setUrlPerfilFocused(false); }}
                        inputRef={input => nomeFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (nomeDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setUrlPerfilDisabled(!nomeDisabled); setUrlPerfilFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                        error={urlNotDefinedError && !newsContext.news.url_perfil}
                        helperText={urlNotDefinedError && !newsContext.news.url_perfil ? 'Campo obrigatório' : ''}
                    />
                </Grid>
            </>
        );
    };

    const getFinalidade = () => {
        return (
            <>
                <Grid item xs={4}>
                    <FormControl
                        variant="outlined"
                        style={{ width: '100%' }}
                    >
                        <InputLabel id="submit-select" style={{ color: 'black' }}>Finalidade</InputLabel>
                        <Select
                            labelId="submit-select"
                            id="submit-select"
                            value={newsContext.news.finalidade}
                            label="Finalidade"
                            onChange={handleChangeSelect('finalidade')}
                        >
                            <MenuItem value={undefined}>Selecione</MenuItem>
                            {filters?.finalidades?.map((item) => (
                                <MenuItem key={item.id} value={item.descricao}>{item.descricao}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </>
        );
    };

    const getMarca = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="marca"
                        value={newsContext.news.marca}
                        onChange={() => { return; }}
                        label="Marca"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens}
                        onBlur={() => { return; }}
                    />
                </Grid>
            </>
        );
    };
    const getPagoOrganico = () => {
        return (
            <>
                <Grid item xs={6}>
                    <Typography variant="subtitle1">Pago ou Orgânico?</Typography>
                    <FormControl fullWidth>
                        <Select
                            labelId="pago_organico_label"
                            id="pago_organico"
                            value={newsContext.news.pagoOrganico.trim()}
                            label="pagoOrganico"
                            className={classes.itens}
                            onChange={handleChangeSelect('pagoOrganico')}
                        >
                            <MenuItem value={'Orgânico'}>Orgânico</MenuItem>
                            <MenuItem value={'Pago'}>Pago</MenuItem>
                            <MenuItem value={'-'} disabled> - </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </>
        );
    };

    const getTipoPerfil = () => {
        return (
            <>
                <Grid item xs={6}>
                    <Typography variant="subtitle1">Tipo de Perfil</Typography>
                    <FormControl fullWidth>
                        <Select
                            labelId="tipo_perfil_label"
                            id="tipo_perfil"
                            value={newsContext.news.tipoPerfil}
                            label="tipoPerfil"
                            className={classes.itens}
                            onChange={handleChangeSelect('tipoPerfil')}
                        >
                            <MenuItem value={'Squad Fixo'}>Squad Fixo</MenuItem>
                            {/* <MenuItem value={'Squad Orgânico'}>Squad Orgânico</MenuItem> */}
                            <MenuItem value={'Squad Orbitante'}>Squad Orbitante</MenuItem>
                            <MenuItem value={'Avulso'}>Avulso</MenuItem>
                            <MenuItem value={'Cross'}>Cross</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </>
        );
    };

    const getObservacaoStatus = () => {
        return (
            <>
                <Grid item xs={6}>
                    <TextField
                        id="StatusObservacao"
                        value={newsContext.news.observacaoStatus ?? ' '}
                        onChange={handleChange('observacaoStatus')}
                        label="Status"
                        variant="outlined"
                        disabled={observacaoStatusDisabled}
                        className={classes.itens}
                        onBlur={() => { setObservacaoStatusDisabled(true); setObservacaoStatusFocused(false); }}
                        inputRef={input => observacaoStatusFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (observacaoStatusDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setObservacaoStatusDisabled(!observacaoStatusDisabled); setObservacaoStatusFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                    />
                </Grid>
            </>
        );
    };

    return (
        <>
            <Grid>
                <Grid
                    container
                    spacing={3}
                    alignItems="flex-start" >
                    {getNomeSolicitante()}
                    {getCampanha()}
                    {getUsuario()}
                    {ObjetivoUso()}
                    {getNomeFiguraPublica()}
                    {getTamanho()}
                    {getUrlPerfil()}
                    {getFinalidade()}
                    {getMarca()}
                    {getPreAssessment()}
                    {getRtb()}
                    {getObservacaoStatus()}
                    {getPagoOrganico()}
                    {getTipoPerfil()}
                </Grid>
            </Grid>
        </>
    );
}